import type WebApiClient from "../../api/WebApiClient.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Contig, type ContigSearch } from "@/graphql.ts";
import {
  CLEAR_CONTIG_SEARCH,
  LOAD_CONTIG_SEARCH_FAILURE,
  LOAD_CONTIG_SEARCH_REQUEST,
  LOAD_CONTIG_SEARCH_SUCCESS,
} from "@/redux/action-types.ts";

/**
 * State definition
 */
const MAX_CONTIGS = 1000;

export interface IContigSearchParameters {
  searchTerm: string;
  genomeId: number;
  limit: number;
}

export interface IContigSearchState {
  isLoading: boolean;
  searchTerm: string;
  genomeId: number;
  limit: number;
  contigs: Contig[];
}

const initialState: IContigSearchState = {
  isLoading: false,
  searchTerm: "",
  genomeId: null,
  limit: MAX_CONTIGS,
  contigs: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [CLEAR_CONTIG_SEARCH]: (_state: IContigSearchState, _payload: any): IContigSearchState => {
    return { ...initialState };
  },
  [LOAD_CONTIG_SEARCH_REQUEST]: (state: IContigSearchState, payload: IContigSearchParameters): IContigSearchState => {
    return { ...state, isLoading: true, ...payload }; // saves the seach conditions
  },
  [LOAD_CONTIG_SEARCH_SUCCESS]: (state: IContigSearchState, payload: ContigSearch): IContigSearchState => {
    if (state.searchTerm === payload.searchTerm) {
      return { ...state, isLoading: false, contigs: payload.contigs };
    }
    return state;
  },
  [LOAD_CONTIG_SEARCH_FAILURE]: (state: IContigSearchState, _payload): IContigSearchState => {
    return { ...state, isLoading: false, contigs: [] };
  },
});

/**
 * Action creators
 */

export function shouldLoadContigsRelatedToAGenome(genomeId: number, searchTerm = "", limit: number = MAX_CONTIGS) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<ContigSearch> => {
    const contigSearchState: IContigSearchState = getState().contigSearch;
    if (contigSearchState.genomeId === genomeId && contigSearchState.searchTerm === searchTerm) {
      return Promise.resolve({ contigs: contigSearchState.contigs, searchTerm } as ContigSearch);
    }

    dispatch({
      type: LOAD_CONTIG_SEARCH_REQUEST,
      payload: { genomeId, searchTerm, limit },
    });

    return webApiClient
      .fetchContigSearchResults(genomeId, searchTerm, limit)
      .then((response: ContigSearch) => {
        dispatch({
          type: LOAD_CONTIG_SEARCH_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CONTIG_SEARCH_FAILURE,
          alertTitle: "Fetching of Contig Options Failed",
        }),
      );
  };
}

export function clearContigSearch() {
  return (dispatch) => {
    // reset the contig search
    dispatch({ type: CLEAR_CONTIG_SEARCH });
  };
}
